import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CheckIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Drawer } from '../ui/Drawer';
import { SelectField, isDeletedFlag, normalizeOptions } from './fields';
import EditHistoryLink from './EditHistoryLink';
import LocationLookupField, { LocationValue } from './LocationLookupField';
import type { CustomDrawerProps } from './TablemaintPage';
import tablemaintService, { TablemaintRow } from '../../services/tablemaint.service';
import { EQUIPMENT_SIZES, EQUIPMENT_TYPES } from '../../constants/orderFieldOptions';
import {
  NewEmptyLine, ScacEmptyBatchRow, ScacEmptyOptions,
  addScacEmptyLines, getScacEmptyBatch, getScacEmptyFormOptions,
} from '../../services/scacempty.service';

/**
 * Bespoke batch editor for SCAC Empty Locations (cms.scacemptylocations). The table is
 * APPEND-ONLY and grouped by effective-date batch (dateindex = TO_DAYS(effectivedatetime)):
 * a row-click opens the whole batch BY ITS DATEINDEX. Existing lines are readonly here (with
 * a Deleted toggle); new lines are added for the batch's effective date via /scacempty/add.
 * Adding "new" (no row) starts a fresh batch — pick an effective date, then add lines.
 *
 * Per-line save validation (mirrors legacy validateSCACEmpty): every filled new line needs
 * SCAC, Terminal, Pickup and Return — incomplete lines are highlighted and block the save.
 */
const TABLE = 'scacempty';

const s = (v: any): string => (v == null ? '' : String(v));
const todayStr = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

interface NewRow {
  key: string;
  scac: string;
  terminal: string;
  chassisprovider: string;
  size: string;
  equiptype: string;
  pickup: LocationValue;
  ret: LocationValue;
}

const blankRow = (key: string): NewRow => ({
  key, scac: '', terminal: '', chassisprovider: '', size: '', equiptype: '',
  pickup: { recno: 0, name: '' }, ret: { recno: 0, name: '' },
});

const ScacEmptyLocationsEditDrawer: React.FC<CustomDrawerProps> = ({ row, onClose, onSaved }) => {
  const isNew = !row;
  const dateindex = row ? Number(row.dateindex) : 0;

  const [opts, setOpts] = useState<ScacEmptyOptions | null>(null);
  const [batchDate, setBatchDate] = useState<string>(isNew ? todayStr() : '');
  const [existing, setExisting] = useState<ScacEmptyBatchRow[]>([]);
  const [deletedState, setDeletedState] = useState<Record<number, boolean>>({});
  const origDeleted = useRef<Record<number, boolean>>({});
  const [newRows, setNewRows] = useState<NewRow[]>([blankRow('n0'), blankRow('n1'), blankRow('n2')]);
  const keyCounter = useRef(3);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [showErrors, setShowErrors] = useState(false);
  const topRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    getScacEmptyFormOptions().then(setOpts).catch(() => {});
    if (!isNew && dateindex) {
      getScacEmptyBatch(dateindex).then((rows) => {
        setExisting(rows);
        if (rows.length) setBatchDate(rows[0].effectivedate);
        const d: Record<number, boolean> = {};
        rows.forEach((r) => { d[r.recno] = r.deleted === 'Y'; });
        origDeleted.current = { ...d };
        setDeletedState(d);
      }).catch(() => {});
    }
  }, [isNew, dateindex]);

  useEffect(() => { if (saveError || showErrors) topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); }, [saveError, showErrors]);

  const scacOpts = useMemo(() => opts?.scacs ?? [], [opts]);
  const terminalOpts = useMemo(() => normalizeOptions(opts?.terminals ?? []), [opts]);
  const poolOpts = useMemo(() => normalizeOptions(opts?.pools ?? []), [opts]);
  const sizeOpts = useMemo(() => normalizeOptions(EQUIPMENT_SIZES as unknown as string[]), []);
  const typeOpts = useMemo(() => normalizeOptions(EQUIPMENT_TYPES as unknown as string[]), []);

  const setRow = (key: string, patch: Partial<NewRow>) =>
    setNewRows((p) => p.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const addRow = () => setNewRows((p) => [...p, blankRow(`n${keyCounter.current++}`)]);
  const removeRow = (key: string) => setNewRows((p) => (p.length > 1 ? p.filter((r) => r.key !== key) : p.map((r) => (r.key === key ? blankRow(r.key) : r))));

  const rowFilled = (r: NewRow) => !!(r.scac || r.terminal || r.pickup.recno || r.ret.recno);
  const rowInvalid = (r: NewRow) => rowFilled(r) && (!r.scac.trim() || !r.terminal.trim() || !r.pickup.recno || !r.ret.recno);
  const filledRows = useMemo(() => newRows.filter(rowFilled), [newRows]);

  const errors = useMemo(() => {
    const e: string[] = [];
    if (filledRows.length && !batchDate.trim()) e.push('Effective Date is required.');
    if (filledRows.some(rowInvalid)) e.push('Each new line needs a SCAC, Terminal, Pickup and Return. Incomplete lines are highlighted.');
    return e;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledRows, batchDate, newRows]);
  const invalidKeys = useMemo(() => new Set(newRows.filter(rowInvalid).map((r) => r.key)), [newRows]);
  const banner = [...(showErrors ? errors : []), ...(saveError ? [saveError] : [])];

  const handleSave = useCallback(async () => {
    if (errors.length) { setShowErrors(true); return; }
    setShowErrors(false);
    setSaveError(null);
    setIsSaving(true);
    try {
      // 1) Append the filled new lines for the batch's effective date.
      if (filledRows.length) {
        const lines: NewEmptyLine[] = filledRows.map((r) => ({
          scac: r.scac.trim(), terminal: r.terminal.trim(), chassisprovider: r.chassisprovider.trim(),
          size: r.size.trim(), equiptype: r.equiptype.trim(),
          pickuplocation: r.pickup.recno, terminatelocation: r.ret.recno,
        }));
        await addScacEmptyLines(batchDate, lines);
      }
      // 2) Apply Deleted-flag changes on existing lines (via the engine update → history).
      for (const rec of existing) {
        const now = !!deletedState[rec.recno];
        if (now !== !!origDeleted.current[rec.recno]) {
          await tablemaintService.update(TABLE, rec.recno, { deleted: now ? 'Y' : 'N' });
        }
      }
      onSaved();
      onClose();
    } catch (err: any) {
      setSaveError(err?.response?.data?.detail || err?.message || 'Failed to save SCAC empty locations.');
    } finally {
      setIsSaving(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, filledRows, batchDate, existing, deletedState, onClose, onSaved]);

  const footer = (
    <div className="flex items-center justify-end gap-3">
      <button onClick={onClose} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-label bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50">
        Cancel
      </button>
      <button onClick={handleSave} disabled={isSaving}
        className="px-4 py-2 text-sm font-medium text-accent-fg bg-accent rounded-lg hover:bg-accent-hover disabled:opacity-50 inline-flex items-center gap-2">
        {isSaving ? (<><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>)
          : (<><CheckIcon className="w-4 h-4" /> Save</>)}
      </button>
    </div>
  );

  const title = isNew ? 'Add SCAC Empty Locations' : 'SCAC Empty Locations Batch';
  const th = 'px-2 py-1.5 text-left text-xs font-semibold text-gray-600 dark:text-gray-300 whitespace-nowrap';
  const td = 'px-2 py-1 align-top text-sm text-gray-800 dark:text-gray-200 whitespace-nowrap';
  // A joined location name whose target has since been soft-deleted: red + "(deleted)",
  // the same marker the grid puts on these columns (registry deletedKey).
  const refCell = (name: string, deletedFlag: string) =>
    (isDeletedFlag(deletedFlag) ? (
      <span className="text-red-600 dark:text-red-400">
        {name}<span className="ml-1.5 text-[0.625rem] font-semibold">(deleted)</span>
      </span>
    ) : name);

  return (
    <Drawer isOpen onClose={onClose} title={title} position="right" width="80rem" footer={footer}>
      <div className="space-y-5">
        <div ref={topRef} />
        {!isNew && dateindex > 0 && (
          <div className="flex justify-end">
            <EditHistoryLink table={TABLE} recno={dateindex} />
          </div>
        )}
        {banner.length > 0 && (
          <div className="rounded-lg border border-red-300 bg-red-50 dark:border-red-700 dark:bg-red-900/30 p-3">
            <ul className="list-disc list-inside text-sm text-red-800 dark:text-red-300">
              {banner.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </div>
        )}

        {/* Effective date for the batch (fixed for an existing batch; picked for a new one) */}
        <div className="max-w-xs">
          <label className="block text-sm font-medium text-label mb-1">
            Effective Date{isNew && <span className="text-red-500"> *</span>}
          </label>
          {isNew ? (
            <input type="date" value={batchDate} onChange={(e) => setBatchDate(e.target.value)} disabled={isSaving}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white" />
          ) : (
            <div className="px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-gray-50 dark:bg-gray-800 text-body">
              {existing[0]?.effectivedatetime || batchDate || '—'}
            </div>
          )}
        </div>

        {/* Existing lines (readonly + Deleted toggle) */}
        {!isNew && (
          <div>
            <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Existing Lines</h3>
            {existing.length === 0 ? (
              <p className="text-sm text-muted">No lines in this batch.</p>
            ) : (
              <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
                <table className="w-full">
                  <thead className="bg-gray-50 dark:bg-gray-800">
                    <tr>
                      {['SCAC', 'Terminal', 'Chass Pool', 'Size', 'Type', 'Pickup', 'Return', 'Effective', 'Deleted', 'Record'].map((h) => (
                        <th key={h} className={th}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {existing.map((e) => (
                      <tr key={e.recno} className={`border-t border-gray-100 dark:border-gray-800 ${deletedState[e.recno] ? 'bg-gray-100 dark:bg-gray-800/60 text-gray-400 line-through' : ''}`}>
                        <td className={td}>{e.scac}</td>
                        <td className={td}>{e.terminal}</td>
                        <td className={td}>{e.chassisprovider}</td>
                        <td className={td}>{e.size}</td>
                        <td className={td}>{e.equiptype}</td>
                        <td className={td}>{refCell(e.pickup_lname, e.pickupdeleted)}</td>
                        <td className={td}>{refCell(e.terminate_lname, e.terminatedeleted)}</td>
                        <td className={td}>{e.effectivedatetime}</td>
                        <td className={`${td} text-center no-underline`}>
                          <input type="checkbox" checked={!!deletedState[e.recno]} disabled={isSaving}
                            onChange={(ev) => setDeletedState((p) => ({ ...p, [e.recno]: ev.target.checked }))} />
                        </td>
                        <td className={`${td} text-gray-500`}>{e.recno}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* New lines (editable) */}
        <div>
          <h3 className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted">Add Lines</h3>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700">
            <table className="w-full">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className={`${th} min-w-[150px]`}>SCAC</th>
                  <th className={`${th} min-w-[130px]`}>Terminal</th>
                  <th className={`${th} min-w-[110px]`}>Chass Pool</th>
                  <th className={`${th} min-w-[90px]`}>Size</th>
                  <th className={`${th} min-w-[130px]`}>Type</th>
                  <th className={`${th} min-w-[180px]`}>Pickup</th>
                  <th className={`${th} min-w-[180px]`}>Return</th>
                  <th className={th} />
                </tr>
              </thead>
              <tbody>
                {newRows.map((r) => {
                  const bad = showErrors && invalidKeys.has(r.key);
                  return (
                    <tr key={r.key} className={`border-t border-gray-100 dark:border-gray-800 ${bad ? 'bg-red-50/60 dark:bg-red-900/20' : ''}`}>
                      <td className="px-2 py-1.5 align-top"><SelectField hideLabel label="SCAC" value={r.scac} onChange={(v) => setRow(r.key, { scac: v })} options={scacOpts} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><SelectField hideLabel label="Terminal" value={r.terminal} onChange={(v) => setRow(r.key, { terminal: v })} options={terminalOpts} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><SelectField hideLabel label="Chass Pool" value={r.chassisprovider} onChange={(v) => setRow(r.key, { chassisprovider: v })} options={poolOpts} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><SelectField hideLabel label="Size" value={r.size} onChange={(v) => setRow(r.key, { size: v })} options={sizeOpts} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><SelectField hideLabel label="Type" value={r.equiptype} onChange={(v) => setRow(r.key, { equiptype: v })} options={typeOpts} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><LocationLookupField hideLabel hideStatus label="Pickup" value={r.pickup} onChange={(v) => setRow(r.key, { pickup: v })} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top"><LocationLookupField hideLabel hideStatus label="Return" value={r.ret} onChange={(v) => setRow(r.key, { ret: v })} disabled={isSaving} /></td>
                      <td className="px-2 py-1.5 align-top text-center">
                        <button type="button" onClick={() => removeRow(r.key)} disabled={isSaving}
                          className="mt-1 inline-flex items-center justify-center rounded-lg border border-gray-300 dark:border-gray-600 p-1.5 text-gray-500 hover:text-red-600 hover:border-red-300 disabled:opacity-50" aria-label="Remove line">
                          <TrashIcon className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <button type="button" onClick={addRow} disabled={isSaving}
            className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-blue-700 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 disabled:opacity-50">
            <PlusIcon className="w-4 h-4" /> Add Line
          </button>
        </div>
      </div>
    </Drawer>
  );
};

export default ScacEmptyLocationsEditDrawer;
